import { Box, Flex, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { Button as DesignButton } from 'src/design-system/Button';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { Select } from 'src/design-system/Select';
import { IconCheck, IconPencil, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { V2MemberRelationship } from './dataHelpers';
import { Relation } from '../hooks/useFamily';

const RELATION_OPTIONS: { value: Relation; label: string }[] = [
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Parent', label: 'Parent' },
  { value: 'Child', label: 'Child' },
  { value: 'Sibling', label: 'Sibling' },
  { value: 'Other', label: 'Other' },
];

type Props = {
  selectedMembers: Client[];
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (familyName: string, relationships: Record<string, V2MemberRelationship>) => void;
  isLoading: boolean;
};

export default function NameAndRelationshipsStep({
  selectedMembers,
  onBack,
  onCancel,
  onSubmit,
  isLoading,
}: Props) {
  const [familyName, setFamilyName] = useState('');
  const [focused, setFocused] = useState(false);
  const [relationships, setRelationships] = useState<Record<string, V2MemberRelationship>>(() =>
    Object.fromEntries(
      selectedMembers.map(m => [
        m.id,
        Object.fromEntries(selectedMembers.filter(o => o.id !== m.id).map(o => [o.id, null])),
      ]),
    ),
  );

  const INVERSE_RELATIONS: Partial<Record<Relation, Relation>> = {
    Spouse: 'Spouse',
    Partner: 'Partner',
    Parent: 'Child',
    Child: 'Parent',
    Sibling: 'Sibling',
    Other: 'Other',
  };

  const setRelation = (fromMemberId: string, toMemberId: string, relation: Relation | null) => {
    setRelationships(prev => {
      const next = {
        ...prev,
        [fromMemberId]: { ...prev[fromMemberId], [toMemberId]: relation },
      };
      const inverse = relation !== null ? INVERSE_RELATIONS[relation] : undefined;
      if (inverse !== undefined) {
        next[toMemberId] = { ...next[toMemberId], [fromMemberId]: inverse };
      }
      return next;
    });
  };

  const allRelationshipsFilled = Object.values(relationships).every(toMap =>
    Object.values(toMap).every(relation => relation !== null),
  );

  const isValid = familyName.trim().length > 0 && allRelationshipsFilled;

  return (
    <Flex direction="column" h="100%">
      <FlowLayout
        header={
          <Box px="xl" pt="xxl" pb="md">
            <Flex justify="space-between" align="center" pb="lg">
              <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
                Name this family and define relationships
              </Text>
              <DesignButton variant="text" style={{ flexShrink: 0 }} onClick={onCancel}>
                Cancel
              </DesignButton>
            </Flex>

            <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.6" mb={6}>
              Family name
            </Text>
            <TextInput
              placeholder="Family name"
              value={familyName}
              onChange={e => setFamilyName(e.currentTarget.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              rightSection={
                familyName.trim().length > 0 ? (
                  <IconCheck size={18} color="var(--mantine-color-green-6)" />
                ) : (
                  <IconX size={18} color="var(--mantine-color-red-6)" />
                )
              }
              radius="xl"
              size="md"
              styles={theme => ({
                wrapper: { height: 48 },
                input: {
                  height: 48,
                  borderRadius: 9999,
                  backgroundColor: theme.white,
                  border: `2px solid ${focused ? theme.colors.brand[6] : theme.colors.gray[2]}`,
                  fontSize: theme.fontSizes.md,
                },
                section: {
                  width: 44,
                  justifyContent: 'center',
                },
              })}
            />
          </Box>
        }
        body={
          <FlowBody
            footerAlign="center"
            footer={
              <FlowNavigationFooter
                onBack={onBack}
                onAction={() => onSubmit(familyName, relationships)}
                actionDisabled={!isValid}
                actionLoading={isLoading}
              />
            }
          >
            <Box px="xs" pb="md">
              <SimpleGrid cols={2} spacing="sm">
                {selectedMembers.map(client => (
                  <Box key={client.id} bg="gray.1" style={{ borderRadius: 20, padding: 20 }}>
                    <Flex justify="space-between" align="flex-start" mb="md">
                      <Box>
                        <Text fw={FONT_WEIGHTS.semibold} size="md" c="gray.10">
                          {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                        </Text>
                        {client.emailAddress && (
                          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
                            {client.emailAddress}
                          </Text>
                        )}
                      </Box>
                      <DesignButton
                        variant={client.advised ? 'tertiary' : 'secondary'}
                        size="sm"
                        rightSection={<IconPencil size={16} color="currentColor" />}
                        style={{ flexShrink: 0 }}
                      >
                        {client.advised ? 'Advised' : 'Not advised'}
                      </DesignButton>
                    </Flex>

                    <Stack gap="xs">
                      {selectedMembers
                        .filter(other => other.id !== client.id)
                        .map(other => (
                          <Flex key={other.id} align="center" gap="sm">
                            <Box style={{ flex: 1, position: 'relative' }}>
                              {relationships[client.id]?.[other.id] === null && (
                                <Text
                                  c="red.6"
                                  size="xs"
                                  fw={FONT_WEIGHTS.semibold}
                                  style={{
                                    position: 'absolute',
                                    top: -6,
                                    right: -4,
                                    zIndex: 1,
                                    lineHeight: 1,
                                  }}
                                >
                                  *
                                </Text>
                              )}
                              <Select
                                placeholder="Select relationship"
                                data={RELATION_OPTIONS}
                                value={relationships[client.id]?.[other.id] ?? null}
                                onChange={val =>
                                  setRelation(client.id, other.id, val as Relation | null)
                                }
                                variant="card"
                                styles={
                                  relationships[client.id]?.[other.id] === null
                                    ? {
                                        input: {
                                          border: '1px solid var(--mantine-color-red-6)',
                                        },
                                      }
                                    : undefined
                                }
                              />
                            </Box>
                            <Text
                              size="sm"
                              fw={FONT_WEIGHTS.medium}
                              c="gray.7"
                              style={{ flexShrink: 0 }}
                            >
                              to {formatName(other.firstName, other.lastName, NameFormat.NATURAL)}
                            </Text>
                          </Flex>
                        ))}
                    </Stack>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </FlowBody>
        }
      />
    </Flex>
  );
}
